import React, { useState, useMemo } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Collapse,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import Switch from "react-switch"

const RoleBasedPermissionSelector = ({
  groupedPermissions,
  selectedPermissions,
  onChange,
  disabledPermissions = [],
}) => {
  const [expandedGroups, setExpandedGroups] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("all")

  // Define role-based permission categories
  const ROLE_CATEGORIES = {
    all: {
      label: "All Permissions",
      icon: "bx-list-check",
      color: "secondary",
      description: "View all available permissions",
    },
    admin: {
      label: "Admin Permissions",
      icon: "bx-shield-alt",
      color: "danger",
      description: "Permissions for Admin role",
      keywords: ["user", "role", "permission", "activity", "action"],
    },
    provider: {
      label: "Provider Permissions",
      icon: "bx-building",
      color: "success",
      description: "Permissions for Provider role",
      keywords: [
        "provider-in",
        "provider-out",
        "consumption",
        "adjustment",
        "report",
      ],
    },
    province: {
      label: "Province Permissions",
      icon: "bx-map",
      color: "info",
      description: "Permissions for Province role",
      keywords: ["province-in", "province-out", "consumption", "receipt", "distribution", "report"],
    },
    monitor: {
      label: "Monitor Permissions",
      icon: "bx-bar-chart",
      color: "warning",
      description: "Permissions for Monitor role",
      keywords: ["monitor", "report", "stock", "receipt", "distribution"],
    },
    system: {
      label: "System Setup",
      icon: "bx-cog",
      color: "primary",
      description: "System configuration permissions",
      keywords: [
        "warehouse",
        "project",
        "facility",
        "item",
        "currency",
        "ip",
        "show-provider",
        "create-provider",
        "update-provider",
        "delete-provider",
        "show-province",
        "update-province",
      ],
    },
  }

  // Categorize permissions based on keywords
  const categorizePermission = (permissionName, resource) => {
    const lowerName = permissionName.toLowerCase()
    const lowerResource = resource.toLowerCase()

    for (const [key, config] of Object.entries(ROLE_CATEGORIES)) {
      if (key === "all") continue
      if (
        config.keywords.some(
          keyword =>
            lowerName.includes(keyword) || lowerResource.includes(keyword)
        )
      ) {
        return key
      }
    }
    return "system"
  }

  // Filter and categorize permissions
  const categorizedPermissions = useMemo(() => {
    if (!groupedPermissions || groupedPermissions.length === 0) return {}

    const categorized = {
      all: [],
      admin: [],
      provider: [],
      province: [],
      monitor: [],
      system: [],
    }

    groupedPermissions.forEach(group => {
      group.permissions.forEach(permission => {
        const category = categorizePermission(permission.value || permission.name, group.resource)
        const permObj = {
          ...permission,
          resource: group.resource,
          display_name: group.display_name,
        }

        categorized.all.push(permObj)
        if (category !== "all") {
          categorized[category].push(permObj)
        }
      })
    })

    // Group by resource within each category
    const grouped = {}
    Object.keys(categorized).forEach(category => {
      grouped[category] = {}
      categorized[category].forEach(perm => {
        if (!grouped[category][perm.resource]) {
          grouped[category][perm.resource] = {
            resource: perm.resource,
            display_name: perm.display_name,
            permissions: [],
          }
        }
        grouped[category][perm.resource].permissions.push(perm)
      })
    })

    return grouped
  }, [groupedPermissions])

  // Toggle expand/collapse for a group
  const toggleGroup = (resource, category) => {
    const key = `${category}-${resource}`
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  // Check if permission is selected
  const isSelected = permissionName => {
    return selectedPermissions.includes(permissionName)
  }

  // Check if permission is disabled
  const isDisabled = permissionName => {
    return disabledPermissions.includes(permissionName)
  }

  // Toggle single permission
  const togglePermission = permissionName => {
    if (isDisabled(permissionName)) return

    if (isSelected(permissionName)) {
      onChange(selectedPermissions.filter(p => p !== permissionName))
    } else {
      onChange([...selectedPermissions, permissionName])
    }
  }

  // Toggle all permissions in a group
  const toggleGroupPermissions = group => {
    const groupPermissionNames = group.permissions.map(p => p.value || p.name)
    const enabledGroupPermissions = groupPermissionNames.filter(
      p => !isDisabled(p)
    )
    const allSelected = enabledGroupPermissions.every(p => isSelected(p))

    if (allSelected) {
      onChange(
        selectedPermissions.filter(p => !groupPermissionNames.includes(p))
      )
    } else {
      const newSelected = [...selectedPermissions]
      enabledGroupPermissions.forEach(p => {
        if (!newSelected.includes(p)) {
          newSelected.push(p)
        }
      })
      onChange(newSelected)
    }
  }

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return categorizedPermissions[activeTab] || {}

    const filtered = {}
    const groups = categorizedPermissions[activeTab] || {}

    Object.keys(groups).forEach(resource => {
      const group = groups[resource]
      const matchedPermissions = group.permissions.filter(
        p =>
          (p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.value?.toLowerCase().includes(searchTerm.toLowerCase())) &&
          (group.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            resource.toLowerCase().includes(searchTerm.toLowerCase()))
      )

      if (matchedPermissions.length > 0) {
        filtered[resource] = {
          ...group,
          permissions: matchedPermissions,
        }
      }
    })

    return filtered
  }, [categorizedPermissions, activeTab, searchTerm])

  const getActionBadgeColor = action => {
    switch (action) {
      case "show":
        return "info"
      case "create":
        return "success"
      case "update":
        return "warning"
      case "delete":
        return "danger"
      case "manage":
        return "primary"
      default:
        return "secondary"
    }
  }

  const Offsymbol = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 12,
        color: "#fff",
        paddingRight: 2,
      }}
    >
      No
    </div>
  )

  const OnSymbol = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 12,
        color: "#fff",
        paddingRight: 2,
      }}
    >
      Yes
    </div>
  )

  if (!groupedPermissions || groupedPermissions.length === 0) {
    return (
      <div className="alert alert-info">
        No permissions available. Please add permissions first.
      </div>
    )
  }

  const groups = filteredGroups || {}
  const groupCount = Object.keys(groups).length

  return (
    <div>
      {/* Role-Based Tabs */}
      <Card className="mb-3">
        <CardBody>
          <Nav tabs>
            {Object.entries(ROLE_CATEGORIES).map(([key, config]) => (
              <NavItem key={key}>
                <NavLink
                  className={activeTab === key ? "active" : ""}
                  onClick={() => {
                    setActiveTab(key)
                    setSearchTerm("")
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <i className={`bx ${config.icon} me-2`}></i>
                  {config.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </CardBody>
      </Card>

      {/* Search and Controls */}
      <Row className="mb-3">
        <Col md={6}>
          <Input
            type="text"
            placeholder="Search permissions or resources..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <Button
            size="sm"
            color="primary"
            outline
            className="me-2"
            onClick={() =>
              setExpandedGroups(
                Object.keys(groups).reduce((acc, key) => {
                  acc[`${activeTab}-${key}`] = true
                  return acc
                }, {})
              )
            }
          >
            Expand All
          </Button>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() =>
              setExpandedGroups(
                Object.keys(groups).reduce((acc, key) => {
                  acc[`${activeTab}-${key}`] = false
                  return acc
                }, {})
              )
            }
          >
            Collapse All
          </Button>
        </Col>
      </Row>

      {/* Role Description */}
      <Card className="mb-3 bg-light">
        <CardBody className="py-2">
          <small className="text-muted">
            <i className="bx bx-info-circle me-2"></i>
            {ROLE_CATEGORIES[activeTab].description}
          </small>
        </CardBody>
      </Card>

      {/* Grouped Permissions */}
      {groupCount === 0 ? (
        <div className="alert alert-warning">
          No permissions match your search.
        </div>
      ) : (
        Object.entries(groups).map(([resource, group], idx) => {
          const groupKey = `${activeTab}-${resource}`
          const groupPermissionNames = group.permissions.map(
            p => p.value || p.name
          )
          const enabledGroupPermissions = groupPermissionNames.filter(
            p => !isDisabled(p)
          )
          const selectedCount = groupPermissionNames.filter(p =>
            isSelected(p)
          ).length
          const isGroupExpanded = expandedGroups[groupKey]

          return (
            <Card key={idx} className="mb-2">
              <CardHeader
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => toggleGroup(resource, activeTab)}
              >
                <div className="d-flex align-items-center flex-grow-1">
                  <i
                    className={`bx ${
                      isGroupExpanded ? "bx-chevron-down" : "bx-chevron-right"
                    } me-2`}
                  ></i>
                  <h6 className="mb-0">
                    <i className="bx bx-lock-alt me-2"></i>
                    {group.display_name || resource}
                  </h6>
                  <Badge color="primary" className="ms-2">
                    {selectedCount}/{group.permissions.length}
                  </Badge>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <Label className="form-label mb-0 me-2">Select All</Label>
                  <Input
                    type="checkbox"
                    checked={
                      enabledGroupPermissions.length > 0 &&
                      enabledGroupPermissions.every(p => isSelected(p))
                    }
                    onChange={() => toggleGroupPermissions(group)}
                    disabled={enabledGroupPermissions.length === 0}
                  />
                </div>
              </CardHeader>
              <Collapse isOpen={isGroupExpanded}>
                <CardBody>
                  <Row>
                    {group.permissions.map((permission, permIdx) => {
                      const permissionName = permission.value || permission.name
                      const action = permission.action || ""
                      const disabled = isDisabled(permissionName)

                      return (
                        <Col md={6} lg={4} key={permIdx} className="mb-3">
                          <div
                            className={`d-flex justify-content-between align-items-center p-2 border rounded ${
                              disabled ? "bg-light" : ""
                            }`}
                          >
                            <div>
                              <code className="text-muted small">
                                {permissionName}
                              </code>
                              <br />
                              <Badge
                                color={getActionBadgeColor(action)}
                                className="mt-1"
                              >
                                {action}
                              </Badge>
                            </div>
                            <Switch
                              uncheckedIcon={<Offsymbol />}
                              checkedIcon={<OnSymbol />}
                              onColor="#626ed4"
                              onChange={() => togglePermission(permissionName)}
                              checked={isSelected(permissionName)}
                              disabled={disabled}
                              height={20}
                              width={48}
                            />
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>
          )
        })
      )}
    </div>
  )
}

export default RoleBasedPermissionSelector
